import React, { useEffect, useMemo, useState } from "react";
import { Button, TextField, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import axios from "axios";
import styles from "./PurchaseConfirmation.module.scss";

type UserResponse = [
  number,
  {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
    middle_name?: string | null;
    title?: string;
    [key: string]: any;
  }
];

function PurchaseConfirmation() {
  const navigate = useNavigate();

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [loadingUser, setLoadingUser] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userLoadError, setUserLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
  });

  const [touched, setTouched] = useState({
    whatsapp: false,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoadingUser(true);
        setUserLoadError("");

        const response = await axios.get<UserResponse>("/user");
        const [, user] = response.data;

        const fullName = [user.first_name, user.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();

        setForm((prev) => ({
          ...prev,
          name: fullName || "",
          email: user.email || "",
        }));
      } catch (error) {
        console.error("Failed to fetch user:", error);
        setUserLoadError("Failed to load user details.");
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, []);

  const whatsappError = useMemo(() => {
    const raw = form.whatsapp.trim();

    if (!raw) return "WhatsApp number is required.";

    // allows +, digits, spaces, dashes, parentheses
    const cleaned = raw.replace(/[^\d+]/g, "");

    // basic international/local validation
    const validPattern = /^(?:\+?\d{10,15})$/;

    if (!validPattern.test(cleaned)) {
      return "Enter a valid WhatsApp number.";
    }

    return "";
  }, [form.whatsapp]);

  const isFormValid =
    form.name.trim() !== "" &&
    form.email.trim() !== "" &&
    whatsappError === "";

  const handleChange =
    (field: "name" | "email" | "whatsapp") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleWhatsappBlur = () => {
    setTouched((prev) => ({ ...prev, whatsapp: true }));
  };

 const handleSubmit = async (event: React.FormEvent) => {
  event.preventDefault();
  setTouched({ whatsapp: true });
  setSubmitError("");

  if (!isFormValid) return;

  try {
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      current_application: "Not specified",
      email: form.email.trim(),
      role: "user",
      mobile_no: form.whatsapp.trim(),
    };

    await axios.post(`${process.env.REACT_APP_BASE_URL}waiting_list`, payload, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

    setSubmitted(true);
  } catch (error) {
    console.error("Failed to save contact details:", error);
    setSubmitError("Failed to save your details. Please try again.");
  } finally {
    setSubmitting(false);
  }
};

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Confetti
        width={windowSize.width}
        height={windowSize.height}
        numberOfPieces={800}
        gravity={0.08}
        wind={0.01}
        recycle={true}
        colors={["#ff9800", "#1976d2", "#ffffff", "#ffcc80"]}
      />

      <motion.div
        className={styles.imageContainer}
        animate={{
          rotate: [0, 8, -8, 6, -6, 0],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <img
          src="/2-removebg.png"
          alt="Success"
          className={styles.successImage}
        />
      </motion.div>

      <motion.h3
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        Purchase Successful 🎉
      </motion.h3>

      <motion.p
  initial={{ y: 10, opacity: 0 }}
  animate={{ y: 0, opacity: 1 }}
  transition={{ delay: 0.6 }}
>
  Your subscription is now active. For assistance and registration, please provide your WhatsApp contact number.
</motion.p>

      {loadingUser ? (
        <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </div>
      ) : userLoadError ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          {userLoadError}
        </Alert>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          style={{ width: "100%", maxWidth: 420, marginTop: 24 }}
        >
          {!submitted ? (
            <form onSubmit={handleSubmit} className={styles.formCard}>
              <TextField
                fullWidth
                margin="normal"
                label="Name"
                value={form.name}
                onChange={handleChange("name")}
                disabled
              />

              <TextField
                fullWidth
                margin="normal"
                label="Email"
                value={form.email}
                onChange={handleChange("email")}
                disabled
              />

             <TextField
                fullWidth
                margin="normal"
                label="WhatsApp Number"
                placeholder="+44 7700 900123"
                value={form.whatsapp}
                onChange={handleChange("whatsapp")}
                onBlur={handleWhatsappBlur}
                error={touched.whatsapp && !!whatsappError}
                helperText={
                  touched.whatsapp
                    ? whatsappError
                    : "For assistance and registration, please provide your WhatsApp contact number."
                }
              />

              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}

              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                style={{ marginTop: 20 }}
              >
                <Button
                  className={styles.glowButton}
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Continue"}
                </Button>
              </motion.div>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className={styles.formCard}
            >
              <Alert severity="success" sx={{ mb: 2 }}>
                Your details have been saved successfully.
              </Alert>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  className={styles.glowButton}
                  variant="contained"
                  onClick={() => navigate("/welcome")}
                  fullWidth
                >
                  Start Onboarding 🚀
                </Button>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

export default PurchaseConfirmation;